<template>
  <div class="pt-[134px] pb-[70px] flex justify-center gap-x-[32px] scene-info">
    <div class="rounded-[12px] overflow-hidden scene-info__left">
      <VideoPlayer
        ref="videoRef"
        :class="`w-full h-full scene-info__video`"
        :url="data.preview_videos[0]"
        :poster="data.image_url"
        :autoplay="true"
        :loop="false"
        :muted="true"
        :enable-fullscreen="false"
        loading="lazy"
        objectFit="cover"
      />
    </div>
    <div class="flex flex-col justify-between w-[400px] scene-info__right">
      <div>
        <div class="flex items-center gap-x-[12px]">
          <h1 class="text-[var(--text-1)] text-[24px] leading-none font-[var(--x-weight-medium)]">
            {{ data.name }}
          </h1>
          <div
            v-if="is3d"
            class="w-[45px] text-center text-[16px] text-[var(--purple-6)] leading-[30px] rounded-[8px] bg-purple-1"
          >
            3D
          </div>
        </div>
        <div class="py-[24px] text-[var(--text-5)] text-[16px] leading-[28px]">
          {{ $t('chang-jing-jie-shao') }}{{ localeColon }}
          {{
            $t(
              'zhe-shi-yi-ge-tagscategory-de-chang-jing-dai-you-tagscategory-de-shi-jue-feng-ge-zhe-ge-chang-jing-ke-yi-man-zu-nin-zai-tagscategory-deng-gong-zuo-zhong-de-ai-shi-pin-chuang-zuo-xu-qiu',
              [
                Tags.category.join('、'),
                Tags.terminal_studio_style.join('、'),
                Tags.application_scene_tag.join('、'),
              ],
            )
          }}
        </div>
        <div class="text-[var(--text-5)] text-[16px] leading-none">
          <div class="scene-info__tag">
            <span class="scene-info__tag-name"
              >{{ $t('yan-bo-shi-lei-xing-0') }}{{ localeColon }}</span
            >
            <div class="scene-info__tag-list">
              <span v-for="name in Tags.category" :key="name">{{ name }}</span>
            </div>
          </div>
          <div class="scene-info__tag py-[16px]">
            <span class="scene-info__tag-name">{{ $t('feng-ge') }}{{ localeColon }}</span>
            <div class="scene-info__tag-list">
              <span v-for="name in Tags.terminal_studio_style" :key="name">
                {{ name }}
              </span>
            </div>
          </div>
          <div class="scene-info__tag">
            <span class="scene-info__tag-name"
              >{{ $t('ying-yong-chang-jing') }}{{ localeColon }}</span
            >
            <div class="scene-info__tag-list">
              <span v-for="name in Tags.application_scene_tag" :key="name">
                {{ name }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-x-[16px]">
        <UpgradeButton
          :text="$t('mian-fei-shi-yong-ci-chang-jing')"
          :scene="true"
          @handle="handleUseScene"
          :configType="data?.config_type"
          style="min-width: 236px"
        />
        <!-- <StartBtn
          :width-style="{ width: '236px' }"
          :text="$t('mian-fei-shi-yong-ci-chang-jing')"
          :handler="handleUseScene"
        /> -->
        <Tooltip>
          <template #content>
            {{ data.is_star_personal ? $t('qu-xiao-shou-cang') : $t('shou-cang') }}
          </template>
          <SceneButton @click="toggleLikeScene" class="w-[58px] h-[58px] text-[20px]">
            <IconLike v-if="data.is_star_personal" />
            <IconUnlike v-else />
          </SceneButton>
        </Tooltip>
        <a-popover
          trigger="click"
          overlay-class-name="a-popover-custom"
          :open="open.popover"
          placement="topRight"
          @open-change="popoverOpenChange"
        >
          <template #content>
            <div class="p-[12px] text-center">
              <div class="pb-[24px] text-[20px] text-[var(--text-1)] leading-[28px]">
                {{ $t('fen-xiang-chang-jing') }}
              </div>
              <StartBtn
                type="small"
                :width-style="{ width: '320px' }"
                :text="$t('fu-zhi-lian-jie')"
                :handler="handleCopy"
              >
                <template #icon>
                  <IconShareLink class="text-[22px]" />
                </template>
              </StartBtn>
            </div>
          </template>
          <Tooltip :open="open.tooltip" @open-change="tooltipOpenChange">
            <template #content>
              {{ $t('fen-xiang') }}
            </template>
            <SceneButton :hovered="open.popover" class="w-[58px] h-[58px] text-[20px]">
              <IconShare />
            </SceneButton>
          </Tooltip>
        </a-popover>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import UpgradeButton from '@/components/upgrade-button/index.vue';
import { replaceSpaceWithUnderscore } from '@rendora/common/utils/url';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { MockSceneDetails } from '~/api/interface/scene';
import IconLike from '~/assets/svg/icon-like.svg?component';
import IconShare from '~/assets/svg/icon-share.svg?component';
import IconUnlike from '~/assets/svg/icon-unlike.svg?component';
import IconShareLink from '~/assets/svg/share-link.svg?component';
import StartBtn from '~/pages/ai-avatar-cn/components/start-btn.vue';
import { copyToClipboard } from '~/utils';

import SceneButton from '../components/button.vue';
import { useScene } from '../hooks';

const props = defineProps<{ data: typeof MockSceneDetails }>();

const { $t } = useNuxtApp();
const { handleUse, handleLike } = useScene();
// const isStarred = ref(props.data.is_star_personal)
const Tags = reactive<{
  [key: string]: string[];
}>({
  category: [],
  terminal_studio_style: [],
  application_scene_tag: [],
});

// 处理分享按钮的 popover + tooltip 互斥
const open = reactive({
  popover: false,
  tooltip: false,
});

const is3d = computed(() => {
  if (Object.prototype.hasOwnProperty.call(props.data.config, 'mesh_screen')) {
    return Object.keys(props.data.config.mesh_screen).length > 0;
  }
  return false;
});

const location = computed(() => {
  if (import.meta.client) {
    return window.location;
  }

  return null;
});

const localeColon = computed(() => {
  return isChinese() ? '：' : ':';
});

function popoverOpenChange(visible: boolean) {
  open.tooltip = false;
  open.popover = visible;
}
function tooltipOpenChange(visible: boolean) {
  if (open.popover) {
    return;
  }
  open.tooltip = visible;
}

function getTags() {
  const outerIds = Object.keys(Tags);
  Object.values(props.data.tag_tree).forEach(item => {
    if (outerIds.includes(item.outer_id)) {
      Tags[item.outer_id] = Object.values(item.children)
        .slice(0, 3)
        .map(item => item.tag_name);
    }
  });
}

function handleUseScene() {
  handleUse(props.data.id);
}
function toggleLikeScene() {
  handleLike(props.data);
}

function handleCopy() {
  const msg = isChinese()
    ? `${location.value?.origin}/studio/${props.data.identity}.html`
    : `${location.value?.origin}/studio/${replaceSpaceWithUnderscore(props.data.name)}-${props.data.identity}.html`;
  copyToClipboard(msg);
  message.success($t('fu-zhi-cheng-gong'));
  open.popover = false;
}

getTags();
</script>
<style lang="scss">
html[lang='en-US'] .scene-info__tag-name {
  margin-right: 8px;
}
</style>

<style lang="scss" scoped>
.scene-info {
  &__left {
    width: 764px;
    height: 441px;
    background-color: var(--x-back-3);
    border: 1px solid var(--x-back-3);
  }
  &__right {
    width: 400px;
    height: 441px;
  }
  &__video {
    :deep(video) {
      object-fit: cover;
    }
  }
  &__tag {
    display: flex;
    align-items: flex-start;
    &-name {
      flex-shrink: 0;
      white-space: nowrap;
      line-height: 30px;
      margin-right: 4px;
    }
    &-list {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      span {
        white-space: nowrap;
        padding: 0 12px;
        color: var(--text-3);
        text-align: center;
        font-size: 16px;
        line-height: 30px;
        border-radius: 8px;
        background-color: var(--back-3);
      }
    }
  }
}

.a-popover-custom {
  .ant-popover-content {
    transform: translateY(8px);
  }
  .ant-popover-arrow {
    display: none;
  }
}

.a-tooltip-custom {
  .ant-tooltip-content {
    transform: translateY(6px);
  }
  .ant-tooltip-arrow::before {
    background-color: #000;
  }
  .ant-tooltip-arrow::after {
    box-shadow: none;
  }
  .ant-tooltip-inner {
    padding: 8px 12px;
    font-size: 13px;
    line-height: 21px;
    text-align: center;
    border-radius: 8px;
    background-color: #000;
    box-shadow: none;
  }
}

@media screen and (min-width: 1600px) and (max-width: 1919.98px) {
  .scene-info__left {
    width: calc(784px + (1024 - 784) * (100vw - 1600px) / (1920 - 1600));
    height: calc(441px + (576 - 441) * (100vw - 1600px) / (1920 - 1600));
  }
  .scene-info__right {
    width: 400px;
    height: calc(441px + (576 - 441) * (100vw - 1600px) / (1920 - 1600));
  }
}

@media screen and (min-width: 1920px) {
  .scene-info__left {
    width: 1024px;
    height: 576px;
  }
  .scene-info__right {
    width: 400px;
    height: 576px;
  }
}
</style>
